import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import {
	removePortal,
	shownPortalIds,
} from "@productlane/lib/src/portal-visibility"
import { assertRankUpdates } from "@productlane/lib/src/roadmap-custom-order"

import type { MutatorTx } from "../schema"
import type {
	HideAllVisibleArgs,
	IssueLinkArgs,
	IssueLinkExistingArgs,
	IssueLinkUpdateArgs,
	IssuesUpsertArgs,
	IssueUnlinkArgs,
	SetPortalSortOrdersArgs,
	UpdateIssueArgs,
	UpdateIssueProjectVisibilityBulkArgs,
} from "../types"

const updateIssue = async (tx: MutatorTx, args: UpdateIssueArgs) => {
	const { state, priority, ...restArgs } = args

	const updateData = {
		...restArgs,
		...(state && {
			status: state.type,
			statusName: state.name,
			statusColor: state.color,
			...(state.id && { statusId: state.id }),
		}),
		...(priority && { priority: priority.label }),
	}

	await tx.mutate.issues.update(updateData)
}

const insertIssueFromLinear = async (
	tx: MutatorTx,
	workspaceId: string,
	id: string,
	linearIssue: {
		id: string
		identifier: string
		priorityLabel: string
		projectId?: string | null
		dueDate: string | number | null
		state: {
			type: string
			name: string
			color: string
		}
		team: {
			id: string
		}
		title: string
		url: string
		description?: string | null
	},
	isVisible: boolean | null | undefined,
) => {
	await tx.mutate.issues.insert({
		id,
		createdAt: Date.now(),
		updatedAt: Date.now(),
		isDeleted: false,
		version: 1,
		linearTicketId: linearIssue.id,
		title: linearIssue.title,
		description: linearIssue.description || "",
		identifier: linearIssue.identifier,
		priority: linearIssue.priorityLabel,
		status: linearIssue.state.type,
		statusName: linearIssue.state.name,
		statusColor: linearIssue.state.color,
		team: linearIssue.team.id,
		url: linearIssue.url,
		projectId: linearIssue.projectId || "",
		sortOrder: 0,
		summaryString: "",
		isVisible,
		dueDate: linearIssue.dueDate
			? new Date(linearIssue.dueDate).getTime()
			: null,
		workspaceId,
		totalScore: 0,
	})
}

export const issueMutators = {
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateIssueArgs }) =>
			updateIssue(tx, args),
	),
	link: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: IssueLinkArgs
			ctx: ZeroJwtPayload
		}) => {
			const linearIssueId = args.linearIssue.id

			try {
				// Check if issue already exists by linearTicketId
				const issues = await tx.query.issues
					.where("linearTicketId", "=", linearIssueId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				const existing = issues[0]

				if (existing) {
					// Update existing issue visibility
					await updateIssue(tx, {
						id: existing.id,
						isVisible:
							existing.isVisible ?? (args.linearIssue.isVisible || false),
					})

					// Add feedback to issue relationship
					await tx.mutate.feedbackToIssues.insert({
						A: args.feedbackId,
						B: existing.id,
					})
				} else {
					// Create new issue if it's a linear type
					if (args.type === "linear") {
						await insertIssueFromLinear(
							tx,
							authData.workspaceId,
							args.issueId,
							args.linearIssue,
							args.linearIssue.isVisible,
						)

						// Add feedback to issue relationship
						await tx.mutate.feedbackToIssues.insert({
							A: args.feedbackId,
							B: args.issueId,
						})
					}
				}
			} catch (error) {
				// Don't throw to allow server-side to handle the operation
			}
		},
	),
	linkUpdate: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: IssueLinkUpdateArgs
			ctx: ZeroJwtPayload
		}) => {
			try {
				// Get the feedback to update
				const [feedback] = await tx.query.feedbacks
					.where("id", "=", args.feedbackId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!feedback) {
					throw new Error("Could not find feedback")
				}

				// Find the customer need
				const customerNeeds = await tx.query.customerNeeds
					.where("feedbackId", "=", args.feedbackId)
					.where("issueId", "=", args.issueId)
					.run()

				const customerNeed = customerNeeds[0]

				if (customerNeed) {
					await tx.mutate.customerNeeds.update({
						id: customerNeed.id,
						priority: args.priority,
					})
				}
			} catch (error) {
				// Don't throw to allow server-side to handle the operation
			}
		},
	),
	linkExisting: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: IssueLinkExistingArgs
			ctx: ZeroJwtPayload
		}) => {
			try {
				// Get the issue to validate it exists
				const [issue] = await tx.query.issues
					.where("id", "=", args.issueId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!issue) {
					throw new Error("Issue not found")
				}

				// Get the feedback to validate it exists
				const [feedback] = await tx.query.feedbacks
					.where("id", "=", args.threadId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!feedback) {
					throw new Error("Could not find feedback")
				}

				// Add feedback to issue relationship
				await tx.mutate.feedbackToIssues.insert({
					A: args.threadId,
					B: args.issueId,
				})
			} catch (error) {
				// Don't throw to allow server-side to handle the operation
			}
		},
	),
	unlink: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: IssueUnlinkArgs
			ctx: ZeroJwtPayload
		}) => {
			try {
				// Get the issue to validate it exists
				const [issue] = await tx.query.issues
					.where("id", "=", args.internalIssueId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!issue) {
					throw new Error("Issue not found")
				}

				// Get the feedback to validate it exists
				const [feedback] = await tx.query.feedbacks
					.where("id", "=", args.feedbackId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!feedback) {
					throw new Error("Could not find feedback")
				}

				// Remove the feedback-to-issue relationship
				const relationships = await tx.query.feedbackToIssues
					.where("A", "=", args.feedbackId)
					.where("B", "=", args.internalIssueId)
					.run()

				for (const relationship of relationships) {
					await tx.mutate.feedbackToIssues.delete({
						A: relationship.A,
						B: relationship.B,
					})
				}
			} catch (error) {
				// Don't throw to allow server-side to handle the operation
			}
		},
	),
	upsert: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: IssuesUpsertArgs
			ctx: ZeroJwtPayload
		}) => {
			// This is just a signal mutation - the real work happens on server side
			for (const linearIssue of args.linearIssues) {
				// Check if issue exists
				const [existingIssue] = await tx.query.issues
					.where("linearTicketId", "=", linearIssue.id)
					.run()

				if (existingIssue) {
					// Update existing issue
					await updateIssue(tx, {
						id: existingIssue.id,
						isVisible: args.internalUpdate.isVisible,
					})
				} else {
					// Create new issue
					await insertIssueFromLinear(
						tx,
						authData.workspaceId,
						linearIssue.internalIssueId,
						linearIssue,
						args.internalUpdate.isVisible,
					)
				}
			}
		},
	),
	hideAllVisible: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: HideAllVisibleArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.issues
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.where("isVisible", "=", true)
				.run()
			const portalId = args.portalInstanceId
			const namedPortalIds = portalId
				? (
						await tx.query.portalInstances
							.where("workspaceId", "=", authData.workspaceId)
							.run()
					).map((p) => p.id)
				: []
			for (const row of rows) {
				if (!portalId) {
					await updateIssue(tx, { id: row.id, isVisible: false })
					continue
				}
				if (!shownPortalIds(row).includes(portalId)) continue
				await updateIssue(tx, {
					id: row.id,
					...removePortal(row, portalId, namedPortalIds),
				})
			}
		},
	),
	bulkChangeVisibility: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateIssueProjectVisibilityBulkArgs
		}) => {
			for (const id of args.ids) {
				await updateIssue(tx, { id, isVisible: args.isVisible })
			}
		},
	),
	setPortalSortOrders: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: SetPortalSortOrdersArgs
			ctx: ZeroJwtPayload
		}) => {
			assertRankUpdates(args.orders, authData.role)
			for (const order of args.orders) {
				await tx.mutate.issues.update({
					id: order.id,
					portalSortOrder: order.portalSortOrder,
				})
			}
		},
	),
}
